// CSV processing utilities for DigitalOcean FinOps Dashboard
import Papa from 'papaparse';
import {
  extractMonetaryValue,
  filterLineItemsByTimeRange,
  getCategoryKey,
  getProductKey,
  getProjectKey,
  parseMonthPeriod
} from './dataUtils';

const DO_API_ORIGIN = 'https://api.digitalocean.com';
const REQUEST_TIMEOUT_MS = 30000;
const MAX_INVOICE_PAGES = 100;
const CSV_CONCURRENCY = 6;

// Error raised for any failed DigitalOcean API call. `kind` lets the UI
// explain what happened instead of showing a raw status code.
export class DigitalOceanApiError extends Error {
  constructor(message, { status = 0, kind = 'http' } = {}) {
    super(message);
    this.name = 'DigitalOceanApiError';
    this.status = status;
    this.kind = kind; // 'auth' | 'forbidden' | 'rate_limit' | 'server' | 'http' | 'network' | 'timeout'
  }
}

const kindForStatus = (status) => {
  if (status === 401) return 'auth';
  if (status === 403) return 'forbidden';
  if (status === 429) return 'rate_limit';
  if (status >= 500) return 'server';
  return 'http';
};

// Human-readable explanation of a failed DigitalOcean request.
export const describeDigitalOceanError = (error) => {
  const kind = error && error.kind;
  switch (kind) {
    case 'auth':
      return 'DigitalOcean rejected this API token. It may have been revoked or mistyped. Remove the account and add it again with a valid token.';
    case 'forbidden':
      return 'This API token is not allowed to read billing data. Create a token with read access to Billing and add the account again.';
    case 'rate_limit':
      return 'DigitalOcean is rate limiting requests for this token. Wait a minute and try again.';
    case 'server':
      return 'DigitalOcean is having trouble right now. Please try again in a few minutes.';
    case 'timeout':
      return 'DigitalOcean took too long to respond. Check your connection and try again.';
    case 'network':
      return 'Unable to reach the DigitalOcean API. Check your internet connection, or whether a browser extension or firewall is blocking api.digitalocean.com.';
    default:
      return 'Unable to load billing data from DigitalOcean. Please try again.';
  }
};

// GET a DigitalOcean API URL with the account token, a timeout, and errors
// normalised to DigitalOceanApiError.
const doApiGet = async (url, token) => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : null;

  let response;
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      signal: controller ? controller.signal : undefined
    });
  } catch (err) {
    if (err && err.name === 'AbortError') {
      throw new DigitalOceanApiError('Request timed out', { kind: 'timeout' });
    }
    throw new DigitalOceanApiError('Network request failed', { kind: 'network' });
  } finally {
    if (timer) clearTimeout(timer);
  }

  if (!response.ok) {
    throw new DigitalOceanApiError(`API Error: ${response.status}`, {
      status: response.status,
      kind: kindForStatus(response.status)
    });
  }
  return response;
};

// Only follow pagination links that stay on the DigitalOcean API, so the
// token is never sent to another host.
const isDigitalOceanUrl = (url) => {
  try {
    return new URL(url).origin === DO_API_ORIGIN;
  } catch (e) {
    return false;
  }
};

// Parse CSV text into structured data
export const parseCSV = (csvText) => {
  try {
    const results = Papa.parse(csvText, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      // Handle quotes and common CSV formatting issues
      delimitersToGuess: [',', '\t', '|', ';']
    });
    return results.data;
  } catch (error) {
    console.error('Error parsing CSV data:', error);
    return [];
  }
};

// Fetch CSV data for a specific invoice. Throws DigitalOceanApiError on failure.
export const fetchInvoiceCSV = async (token, invoiceId) => {
  const response = await doApiGet(
    `${DO_API_ORIGIN}/v2/customers/my/invoices/${encodeURIComponent(invoiceId)}/csv`,
    token
  );
  const csvText = await response.text();
  if (!csvText || csvText.trim().length === 0) return [];
  return parseCSV(csvText);
};

// Fetch the raw CSV text for an invoice (used for per-invoice downloads).
export const fetchInvoiceCSVText = async (token, invoiceId) => {
  const response = await doApiGet(
    `${DO_API_ORIGIN}/v2/customers/my/invoices/${encodeURIComponent(invoiceId)}/csv`,
    token
  );
  return response.text();
};

// Run `worker` over `items` with at most `limit` in flight.
const mapWithConcurrency = async (items, limit, worker) => {
  const results = new Array(items.length);
  let nextIndex = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (nextIndex < items.length) {
      const index = nextIndex++;
      results[index] = await worker(items[index], index);
    }
  });
  await Promise.all(runners);
  return results;
};

// Fetch all invoices and their CSV data.
// Throws if the invoice list cannot be loaded. Individual invoice CSVs that
// fail are reported in `failedInvoices` so the caller can tell the user the
// figures are incomplete instead of silently showing partial totals.
export const fetchAllInvoiceData = async (token, { onProgress } = {}) => {
  const invoicesList = await fetchInvoicesList(token);

  let completed = 0;
  let firstError = null;
  const failedInvoices = [];
  if (onProgress) onProgress(0, invoicesList.length);

  const perInvoice = await mapWithConcurrency(invoicesList, CSV_CONCURRENCY, async (invoice) => {
    let items = [];
    try {
      const csvData = await fetchInvoiceCSV(token, invoice.invoice_uuid);
      // Add invoice information to each line item
      items = csvData.map(item => ({
        ...item,
        invoice_uuid: invoice.invoice_uuid,
        invoice_period: invoice.invoice_period,
        invoice_amount: invoice.amount, // The invoice's total amount, for reference
        date: invoice.date || invoice.created_at
      }));
    } catch (err) {
      console.error(`Error fetching CSV for invoice ${invoice.invoice_uuid}:`, err);
      if (!firstError) firstError = err;
      failedInvoices.push(invoice.invoice_uuid);
    }
    completed++;
    if (onProgress) onProgress(completed, invoicesList.length);
    return items;
  });

  // If every invoice failed the problem is the token or the network, not one invoice.
  if (invoicesList.length > 0 && failedInvoices.length === invoicesList.length) {
    throw firstError;
  }

  return {
    invoices: invoicesList,
    lineItems: perInvoice.flat(),
    failedInvoices
  };
};

// Fetch list of invoices (all pages). Throws DigitalOceanApiError on failure.
export const fetchInvoicesList = async (token) => {
  let fetchedInvoices = [];
  let pageUrl = `${DO_API_ORIGIN}/v2/customers/my/invoices?per_page=100`;
  let pagesFetched = 0;

  while (pageUrl && pagesFetched < MAX_INVOICE_PAGES) {
    const response = await doApiGet(pageUrl, token);
    pagesFetched++;

    let data;
    try {
      data = await response.json();
    } catch (e) {
      throw new DigitalOceanApiError('Unexpected response from DigitalOcean', { kind: 'http' });
    }
    const pageInvoices = (data && data.invoices) || [];
    fetchedInvoices = [...fetchedInvoices, ...pageInvoices];

    let nextUrl = null;
    if (data && data.links && data.links.pages && data.links.pages.next) {
      nextUrl = data.links.pages.next;
    } else if (response.headers && response.headers.get('Link')) {
      const linkHeader = response.headers.get('Link');
      const headerValue = typeof linkHeader === 'string' ? linkHeader : '';

      if (headerValue && headerValue.length <= 2048) {
        const nextSegment = headerValue.split(',').find(segment => segment.includes('rel="next"'));
        if (nextSegment) {
          const start = nextSegment.indexOf('<');
          const end = nextSegment.indexOf('>', start + 1);
          if (start !== -1 && end !== -1 && end > start + 1) {
            nextUrl = nextSegment.substring(start + 1, end);
          }
        }
      }
    }

    pageUrl = nextUrl && nextUrl !== pageUrl && isDigitalOceanUrl(nextUrl) ? nextUrl : null;
  }

  return fetchedInvoices;
};

// Process CSV data for visualizations with optional time range filter (includes discounts)
export const processCSVDataForVisualizations = (lineItems, timeRange = null) => {
  if (!lineItems || lineItems.length === 0) {
    return createEmptyVisualizationData();
  }

  // Apply time range filter if provided
  let itemsToProcess = lineItems;
  if (timeRange) {
    itemsToProcess = filterLineItemsByTimeRange(lineItems, timeRange);
  }

  // Initialize data structures
  const monthlySpend = {};
  const categorySpend = {};
  const projectSpend = {};
  const productSpend = {};
  let totalAmount = 0;
  let validItemCount = 0;
  let discountItemCount = 0;
  let totalDiscountAmount = 0;

  // Process each line item
  itemsToProcess.forEach((item) => {
    const amount = extractMonetaryValue(item);

    // Track discount items separately
    if (amount < 0) {
      discountItemCount++;
      totalDiscountAmount += amount;
    }

    // Negative amounts are not skipped - they represent discounts.

    // For monthly data - use invoice_period or extract from start date
    let month = item.invoice_period || '';
    if (!month && item.start) {
      const date = new Date(item.start);
      if (!isNaN(date.getTime())) {
        month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      }
    }

    if (month) {
      monthlySpend[month] = (monthlySpend[month] || 0) + amount;
    }

    const category = getCategoryKey(item);
    categorySpend[category] = (categorySpend[category] || 0) + amount;

    const project = getProjectKey(item);
    projectSpend[project] = (projectSpend[project] || 0) + amount;

    const product = getProductKey(item);
    productSpend[product] = (productSpend[product] || 0) + amount;

    // Add to total (including negative amounts for discounts)
    totalAmount += amount;
    validItemCount++;
  });

  // Check if we have any data
  if (validItemCount === 0) {
    // Fallback: if no valid items were found, try using invoice totals instead
    return processInvoiceTotals(itemsToProcess);
  }
  
  // Sort monthly data by date for time-series display
  const monthKeys = Object.keys(monthlySpend);
  const sortedMonths = monthKeys.sort((a, b) => {
    // Extract YYYY-MM from period strings like "2023-01" or "January 2023"
    const dateA = parseMonthPeriod(a);
    const dateB = parseMonthPeriod(b);
    return dateA - dateB;
  });
  
  const monthlyLabels = sortedMonths;
  const monthlyValues = sortedMonths.map(month => monthlySpend[month]);
  
  // Calculate trend and forecast with improved methods
  const { trendText, forecastAmount, confidenceText } = 
    calculateTrendAndForecast(monthlyLabels, monthlyValues, validItemCount);
  
  return {
    monthlyData: { labels: monthlyLabels, values: monthlyValues },
    categoryData: categorySpend,
    projectData: projectSpend,
    productData: productSpend,
    summary: {
      totalAmount,
      invoiceCount: Object.keys(monthlySpend).length,
      totalItems: validItemCount,
      discountItems: discountItemCount,
      totalDiscountAmount: totalDiscountAmount,
      trendText,
      forecastAmount,
      confidenceText
    }
  };
};

// Fallback: process invoice totals instead of line items (accounting for discounts)
const processInvoiceTotals = (lineItems) => {
  // Group by invoice and use the invoice totals
  const invoiceAmounts = {};
  const invoices = new Set();
  const projectSpend = {'All Projects': 0};
  const categorySpend = {'All Services': 0};
  const productSpend = {'All Products': 0};
  let totalAmount = 0;
  
  // Use invoice_amount field that we added during processing
  lineItems.forEach(item => {
    if (!item.invoice_uuid || !item.invoice_period) return;
    
    // Skip if we've already processed this invoice
    if (invoices.has(item.invoice_uuid)) return;
    
    // For invoice amounts, make sure to parse correctly if it's a string
    let amount = 0;
    if (item.invoice_amount) {
      if (typeof item.invoice_amount === 'string') {
        const cleaned = item.invoice_amount.replace(/[^\d.-]/g, '');
        amount = Number.parseFloat(cleaned);
      } else if (typeof item.invoice_amount === 'number') {
        amount = item.invoice_amount;
      }
    }
    
    // Don't skip negative amounts anymore - they could be credit invoices
    if (isNaN(amount)) return;
    
    invoices.add(item.invoice_uuid);
    invoiceAmounts[item.invoice_period] = (invoiceAmounts[item.invoice_period] || 0) + amount;
    totalAmount += amount;
    
    // Add to single categories
    projectSpend['All Projects'] += amount;
    categorySpend['All Services'] += amount;
    productSpend['All Products'] += amount;
  });
  
  // Sort months
  const monthKeys = Object.keys(invoiceAmounts);
  const sortedMonths = monthKeys.sort((a, b) => {
    const dateA = parseMonthPeriod(a);
    const dateB = parseMonthPeriod(b);
    return dateA - dateB;
  });
  
  const monthlyLabels = sortedMonths;
  const monthlyValues = sortedMonths.map(month => invoiceAmounts[month]);
  
  // If still no data, return empty result
  if (totalAmount === 0 && monthlyValues.every(val => val === 0)) {
    return createEmptyVisualizationData();
  }
  
  // Calculate trend and forecast with improved methods
  const { trendText, forecastAmount, confidenceText } = 
    calculateTrendAndForecast(monthlyLabels, monthlyValues, invoices.size);
  
  return {
    monthlyData: { labels: monthlyLabels, values: monthlyValues },
    categoryData: categorySpend,
    projectData: projectSpend,
    productData: productSpend,
    summary: {
      totalAmount,
      invoiceCount: invoices.size,
      totalItems: lineItems.length,
      discountItems: 0, // We don't have detailed discount info in this fallback
      totalDiscountAmount: 0,
      trendText,
      forecastAmount,
      confidenceText
    }
  };
};

// Create empty data structures when no data is available
const createEmptyVisualizationData = () => {
  return {
    monthlyData: { labels: [], values: [] },
    categoryData: {'No Data': 0},
    projectData: {'No Data': 0},
    productData: {'No Data': 0},
    summary: {
      totalAmount: 0,
      invoiceCount: 0,
      totalItems: 0,
      discountItems: 0,
      totalDiscountAmount: 0,
      trendText: 'N/A',
      forecastAmount: 0,
      confidenceText: 'No data available'
    }
  };
};

// Calculate trend and forecast from monthly data (discounts are part of the monthly values)
const calculateTrendAndForecast = (labels, values, itemCount) => {
  // Handle the case where we don't have enough data
  if (!labels || !values || values.length === 0) {
    return {
      trendText: 'N/A',
      forecastAmount: 0,
      confidenceText: `No data available`
    };
  }
  
  // Special case: If we're looking at only the last month (timeRange = '1month')
  // we should still make a forecast by looking at a wider range of data
  let lastSpend = 0;
  let prevSpend = 0;
  let fullDataset = false;
  
  // Case 1: We have at least 2 months of data in the current view
  if (values.length >= 2) {
    const lastIndex = values.length - 1;
    lastSpend = values[lastIndex];
    prevSpend = values[lastIndex - 1];
    fullDataset = true;
  } 
  // Case 2: We only have 1 month in view (likely because timeRange = '1month')
  // In this case, we'll use the last month's value and make a simple projection
  else if (values.length === 1) {
    lastSpend = values[0];
    
    // Even with only one month in view, we'll attempt a forecast
    // Set a reasonable default value (for now, just use the last month)
    // We'll refine this in the forecast calculation
    prevSpend = lastSpend;
    
    // Mark that we don't have a full dataset to calculate trend
    fullDataset = false;
  }
  // Case 3: No data available at all
  else {
    return {
      trendText: 'N/A',
      forecastAmount: 0,
      confidenceText: `Insufficient data (${itemCount} items)`
    };
  }

  // Calculate trend, but only if we have at least 2 months of data
  let trendText = 'N/A';
  if (fullDataset && prevSpend !== 0) { // Changed from prevSpend > 0 to handle negative previous spend
    const change = lastSpend - prevSpend;
    const percentChange = (change / Math.abs(prevSpend)) * 100; // Use absolute value for percentage calculation
    trendText = change >= 0 ? 
      `Up ${percentChange.toFixed(1)}%` : 
      `Down ${Math.abs(percentChange).toFixed(1)}%`;
  }

  // IMPROVED FORECASTING METHODS (now accounts for discounts in calculations)
  let forecastAmount = 0;
  let confidenceText = '';

  // For limited visible data
  if (!fullDataset) {
    forecastAmount = lastSpend * 1.03; // Assume 3% growth instead of 5%
    confidenceText = 'Based on limited visible data, high variance possible';
  }
  // For 12+ months of data
  else if (values.length >= 12) {
    // IMPROVED METHOD FOR 12+ MONTHS: Multiple model ensemble
    
    // 1. Calculate moving average growth rates (last 3, 6, and 12 months)
    const last3MonthsAvg = values.slice(-3).reduce((sum, val) => sum + val, 0) / 3;
    const last6MonthsAvg = values.slice(-6).reduce((sum, val) => sum + val, 0) / 6;
    
    // 2. Calculate month-over-month growth rates
    const recentGrowthRates = [];
    for (let i = 1; i < Math.min(6, values.length); i++) {
      if (values[values.length - i - 1] !== 0) { // Changed from > 0 to !== 0
        recentGrowthRates.push(values[values.length - i] / values[values.length - i - 1]);
      }
    }
    
    // Average of recent growth rates
    const avgGrowthRate = recentGrowthRates.length > 0 ? 
      recentGrowthRates.reduce((sum, rate) => sum + rate, 0) / recentGrowthRates.length : 1;
    
    // 3. Calculate seasonal index (compare current month to trailing average)
    // For 12-month view, we need to avoid comparing with ourselves
    let seasonalFactor = 1;
    if (values.length > 12) {
      // Use true seasonal comparison with prior year
      const sameMonthLastYear = values[values.length - 12];
      const trailingAvgLastYear = values.slice(-15, -9).reduce((sum, val) => sum + val, 0) / 6;
      
      if (trailingAvgLastYear !== 0) { // Changed from > 0 to !== 0
        // How much this month typically differs from the average
        seasonalFactor = (sameMonthLastYear / trailingAvgLastYear);
      }
    } else {
      // For exactly 12 months, use the overall pattern in the data
      // rather than comparing with ourselves
      const firstHalfAvg = values.slice(0, 6).reduce((sum, val) => sum + val, 0) / 6;
      const secondHalfAvg = values.slice(-6).reduce((sum, val) => sum + val, 0) / 6;
      
      if (firstHalfAvg !== 0) { // Changed from > 0 to !== 0
        const overallGrowthFactor = secondHalfAvg / firstHalfAvg;
        // Apply a dampened growth factor
        seasonalFactor = Math.pow(overallGrowthFactor, 1/6); // Sixth root for monthly growth
      }
    }
    
    // Cap seasonal factor to avoid extreme values
    seasonalFactor = Math.max(0.8, Math.min(1.2, seasonalFactor));
    
    // 4. Calculate variance to determine confidence
    const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
    const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
    const coefficientOfVariation = Math.abs(mean) > 0 ? Math.sqrt(variance) / Math.abs(mean) : 0; // Use absolute value for mean
    
    // 5. Ensemble forecast using all factors
    // Weighted blend of different models
    const trendModelForecast = lastSpend * avgGrowthRate;
    const movingAvgForecast = (last3MonthsAvg * 0.7 + last6MonthsAvg * 0.3) * seasonalFactor;
    
    // Final forecast with weightings
    forecastAmount = trendModelForecast * 0.6 + movingAvgForecast * 0.4;
    
    // Confidence text based on actual variance in the data
    const confidencePercent = Math.min(25, Math.max(5, Math.round(coefficientOfVariation * 100)));
    confidenceText = `Based on ${values.length} months with seasonal adjustment, ±${confidencePercent}%`;
  } 
  else if (values.length >= 6) {
    // IMPROVED METHOD FOR 6-11 MONTHS
    
    // Calculate exponentially weighted moving average
    // More weight to recent months
    const weights = [0.35, 0.25, 0.15, 0.10, 0.08, 0.07]; // Sum = 1
    let weightedSum = 0;
    let weightSum = 0;
    
    for (let i = 0; i < Math.min(6, values.length); i++) {
      weightedSum += values[values.length - 1 - i] * weights[i];
      weightSum += weights[i];
    }
    
    const weightedAvg = weightedSum / weightSum;
    
    // Calculate growth rate with longer-term trend
    const firstHalf = values.slice(0, Math.floor(values.length / 2));
    const secondHalf = values.slice(Math.floor(values.length / 2));
    
    const firstHalfAvg = firstHalf.reduce((sum, val) => sum + val, 0) / firstHalf.length;
    const secondHalfAvg = secondHalf.reduce((sum, val) => sum + val, 0) / secondHalf.length;
    
    let growthFactor = 1.02; // Default modest growth
    
    if (firstHalfAvg !== 0) { // Changed from > 0 to !== 0
      // Calculate monthly growth factor
      growthFactor = Math.pow(secondHalfAvg / firstHalfAvg, 1/secondHalf.length);
      // Cap growth to avoid extreme forecasts
      growthFactor = Math.max(0.9, Math.min(1.1, growthFactor));
    }
    
    // Retrending method: Weighted average × growth factor
    forecastAmount = weightedAvg * growthFactor;
    
    // Calculate variance for confidence
    const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
    const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
    const relativeVariance = Math.abs(mean) > 0 ? Math.sqrt(variance) / Math.abs(mean) : 0; // Use absolute value for mean
    
    const confidencePercent = Math.min(20, Math.max(8, Math.round(relativeVariance * 100)));
    confidenceText = `Based on ${values.length} months trend analysis, ±${confidencePercent}%`;
  }
  else if (values.length >= 3) {
    // IMPROVED METHOD FOR 3-5 MONTHS
    // Enhanced linear regression with drift adjustment
    
    // 1. Simple linear regression
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
    const n = values.length;
    
    for (let i = 0; i < n; i++) {
      sumX += i;
      sumY += values[i];
      sumXY += i * values[i];
      sumX2 += i * i;
    }
    
    // Calculate slope and intercept
    const denominator = n * sumX2 - sumX * sumX;
    const slope = denominator ? (n * sumXY - sumX * sumY) / denominator : 0;
    const intercept = (sumY - slope * sumX) / n;
    
    // Base projection
    let baseProjection = intercept + slope * n;
    
    // 2. Calculate acceleration/deceleration
    let acceleration = 0;
    if (n >= 3) {
      const firstDiffs = [];
      for (let i = 1; i < n; i++) {
        firstDiffs.push(values[i] - values[i-1]);
      }
      
      // Calculate average change in differences (acceleration)
      let diffSum = 0;
      for (let i = 1; i < firstDiffs.length; i++) {
        diffSum += firstDiffs[i] - firstDiffs[i-1];
      }
      
      if (firstDiffs.length > 1) {
        acceleration = diffSum / (firstDiffs.length - 1);
        // Dampen the acceleration effect
        acceleration *= 0.5;
      }
    }
    
    // Apply acceleration adjustment
    forecastAmount = baseProjection + acceleration;
    
    // Apply reasonable bounds (now considering negative values for discounts)
    const lastValue = values[n-1];
    if (forecastAmount < lastValue * 0.5 && lastValue > 0) forecastAmount = lastValue * 0.9;
    if (forecastAmount > lastValue * 2 && lastValue > 0) forecastAmount = lastValue * 1.3;
    
    // Calculate error bounds based on regression residuals
    let sumSquaredErrors = 0;
    for (let i = 0; i < n; i++) {
      const predicted = intercept + slope * i;
      sumSquaredErrors += Math.pow(values[i] - predicted, 2);
    }
    
    const standardError = Math.sqrt(sumSquaredErrors / (n - 2));
    const meanValue = sumY / n;
    const confidencePercent = Math.abs(meanValue) > 0 ? 
      Math.min(25, Math.max(10, Math.round((standardError / Math.abs(meanValue)) * 100))) : 15;
    
    confidenceText = `Based on ${n} months regression analysis, ±${confidencePercent}%`;
  } 
  else if (values.length >= 2) {
    // IMPROVED METHOD FOR 2 MONTHS (now handles negative values properly)
    // Use dampened growth with reasonability checks
    
    const growthRate = prevSpend !== 0 ? lastSpend / prevSpend : 1;
    
    // Apply stronger dampening for extreme growth rates
    let dampening = 0.7; // Default dampening factor
    if (growthRate > 1.5 || growthRate < 0.75) {
      dampening = 0.5; // More aggressive dampening for extreme changes
    }
    
    const dampedGrowthRate = 1 + (growthRate - 1) * dampening;
    forecastAmount = lastSpend * dampedGrowthRate;
    
    // Apply more conservative bounds (considering negative values)
    const absLastSpend = Math.abs(lastSpend);
    if (Math.abs(forecastAmount) > absLastSpend * 1.3) {
      forecastAmount = lastSpend * (lastSpend >= 0 ? 1.3 : 0.7);
    }
    
    confidenceText = 'Based on 2 months of data, high uncertainty (±20%)';
  }

  // Apply anomaly detection and smoothing for all methods (now considers negative values)
  if (values.length >= 4) {
    const sortedValues = [...values].sort((a, b) => a - b);
    const medianValue = sortedValues[Math.floor(values.length / 2)];
    const threeMonthAvg = (values[values.length - 2] + values[values.length - 3] + values[values.length - 4]) / 3;
    
    // Check if the last month is an outlier compared to both median and recent average
    const medianDeviation = Math.abs(medianValue) > 0 ? Math.abs(lastSpend - medianValue) / Math.abs(medianValue) : 0;
    const recentDeviation = Math.abs(threeMonthAvg) > 0 ? Math.abs(lastSpend - threeMonthAvg) / Math.abs(threeMonthAvg) : 0;
    
    // If both deviations are high, it's likely an anomaly
    if (medianDeviation > 0.3 && recentDeviation > 0.25) {
      // Use a blend that reduces the impact of the anomaly
      const blendedBaseline = threeMonthAvg * 0.7 + medianValue * 0.3;
      // Adjust the forecast by blending the original with the adjusted baseline
      forecastAmount = forecastAmount * 0.4 + blendedBaseline * 0.6;
      confidenceText += ' (adjusted for potential anomaly)';
    }
  }

  return { trendText, forecastAmount, confidenceText };
};
